import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
} from '../components/legacy-ball/table-management/designTokens';

const PICKUP_LOCATIONS = ['Hotel', 'Symposium', 'Meet_and_Greet', 'Ball_Door'] as const;
type PickupLocation = typeof PICKUP_LOCATIONS[number];

const LOCATION_LABELS: Record<PickupLocation, string> = {
  Hotel: 'Hotel',
  Symposium: 'Symposium',
  Meet_and_Greet: 'Meet & Greet',
  Ball_Door: 'Ball Door',
};

interface GuestSearchResult {
  guestRecordId: string;
  fullName: string;
  ticketNumber: string;
  chapterAffiliation: string | null;
  dietaryPreference: string | null;
  isCheckedIn: boolean;
  seatId: string | null;
}

interface PickupStats {
  byLocation: Record<string, number>;
  totalPickups: number;
  totalExpected: number;
}

const STORAGE_KEY = 'packetPickupLocation';

export function PacketPickupPage() {
  const { user } = useAuth();
  const isOrganizerPlus = user ? isAtLeast(user.role, 'Organizer') : false;

  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<GuestSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState<'guest' | 'order'>('guest');
  const [orderLabel, setOrderLabel] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<PickupLocation | null>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && PICKUP_LOCATIONS.includes(stored as PickupLocation)) {
      return stored as PickupLocation;
    }
    return null;
  });
  const [stats, setStats] = useState<PickupStats | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);

  // Fetch stats on mount and after successful pickup
  const fetchStats = useCallback(async () => {
    try {
      const data = await apiClient<PickupStats>('/packet-pickup/stats');
      setStats(data);
    } catch {
      // Non-critical — stats may not load initially
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Auto-focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Persist location selection
  const handleLocationChange = (loc: PickupLocation) => {
    setSelectedLocation(loc);
    localStorage.setItem(STORAGE_KEY, loc);
  };

  // Search on Enter key
  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setResults([]);
      setOrderLabel(null);
      return;
    }
    setLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      if (searchMode === 'order') {
        const data = await apiClient<{ results: GuestSearchResult[] }>(
          `/packet-pickup/search-by-order?orderNumber=${encodeURIComponent(query.trim())}`
        );
        setResults(data.results);
        setOrderLabel(query.trim());
        if (data.results.length === 0) {
          setErrorMessage('No guests found for that order number.');
        }
      } else {
        const data = await apiClient<{ results: GuestSearchResult[] }>(
          `/packet-pickup/search?q=${encodeURIComponent(query.trim())}`
        );
        setResults(data.results);
        setOrderLabel(null);
        if (data.results.length === 0) {
          setErrorMessage('No guests found matching your search.');
        }
      }
    } catch (err: any) {
      if (err?.statusCode === 404) {
        setErrorMessage('No guest found with that search criteria.');
      } else {
        setErrorMessage('Search failed. Please try again.');
      }
      setResults([]);
      setOrderLabel(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(searchQuery);
    }
  };

  const handleConfirmPickup = async (guest: GuestSearchResult) => {
    if (!selectedLocation) return;
    setConfirmingId(guest.guestRecordId);
    setErrorMessage(null);
    setSuccessMessage(null);
    try {
      const resp = await apiClient<{ delivery?: { enqueued?: boolean; skipped?: boolean; queueName?: string } }>('/packet-pickup', {
        method: 'POST',
        body: {
          guestRecordId: guest.guestRecordId,
          pickupLocation: selectedLocation,
        },
      });
      const d = resp?.delivery;
      const queueNote = d?.enqueued
        ? ` · 📦 Added to delivery queue${d.queueName ? ` (${d.queueName})` : ''}`
        : d?.skipped
        ? ' · 📦 Already in delivery queue'
        : '';
      setSuccessMessage(`✅ ${guest.fullName} (${guest.ticketNumber}) — Packet confirmed at ${LOCATION_LABELS[selectedLocation]}${queueNote}`);
      // Mark this guest as checked in locally (keep results visible)
      setResults((prev) =>
        prev.map((g) =>
          g.guestRecordId === guest.guestRecordId ? { ...g, isCheckedIn: true } : g
        )
      );
      fetchStats();
    } catch (err: any) {
      if (err?.statusCode === 409) {
        setErrorMessage(`⚠️ ${guest.fullName} has already picked up their packet.`);
        // Mark as checked in locally too
        setResults((prev) =>
          prev.map((g) =>
            g.guestRecordId === guest.guestRecordId ? { ...g, isCheckedIn: true } : g
          )
        );
      } else if (err?.statusCode === 404) {
        setErrorMessage('Guest not found. Please try searching again.');
      } else if (err?.statusCode === 422) {
        setErrorMessage('This guest has no registration linked. Please contact an organizer.');
      } else {
        setErrorMessage(err?.message || 'Failed to process pickup. Please try again.');
      }
    } finally {
      setConfirmingId(null);
    }
  };

  const locationCount = selectedLocation && stats?.byLocation
    ? (stats.byLocation[selectedLocation] || 0)
    : null;

  const percentProcessed = stats && stats.totalExpected > 0
    ? Math.round((stats.totalPickups / stats.totalExpected) * 100)
    : 0;

  const handlePrintReport = () => {
    if (!stats) return;
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

    const locationRows = PICKUP_LOCATIONS.map((loc) => {
      const count = stats.byLocation[loc] || 0;
      return `<tr><td style="padding: 6px 10px; border-bottom: 1px solid #ddd; font-size: 12px;">${LOCATION_LABELS[loc]}</td><td style="padding: 6px 10px; border-bottom: 1px solid #ddd; font-size: 12px; text-align: right;">${count}</td></tr>`;
    }).join('');

    const pct = stats.totalExpected > 0
      ? Math.round((stats.totalPickups / stats.totalExpected) * 100)
      : 0;

    const html = `<!DOCTYPE html><html><head>
      <title>Packet Pickup Report</title>
      <style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 20px; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        th { text-align: left; padding: 8px 10px; border-bottom: 2px solid #333; font-size: 13px; }
        td { padding: 6px 10px; border-bottom: 1px solid #ddd; font-size: 12px; }
        h1 { font-size: 18px; margin-bottom: 4px; color: #1a6b3c; }
        .subtitle { color: #666; font-size: 12px; margin-bottom: 16px; }
        .summary { margin-bottom: 20px; padding: 12px; background: #f0fdf4; border-radius: 6px; border: 1px solid #bbf7d0; }
        .summary-line { font-size: 14px; margin: 4px 0; }
        .bold { font-weight: 700; }
      </style>
    </head><body>
      <h1>📦 Packet Pickup Report</h1>
      <p class="subtitle">${dateStr} at ${timeStr}</p>
      <div class="summary">
        <p class="summary-line"><span class="bold">${stats.totalPickups}</span> of <span class="bold">${stats.totalExpected}</span> packets picked up (<span class="bold">${pct}%</span> processed)</p>
      </div>
      <table>
        <thead><tr><th>Location</th><th style="text-align: right;">Pickups</th></tr></thead>
        <tbody>
          ${locationRows}
          <tr style="font-weight: 700; border-top: 2px solid #333;">
            <td style="padding: 8px 10px; font-size: 13px;">Total</td>
            <td style="padding: 8px 10px; font-size: 13px; text-align: right;">${stats.totalPickups}</td>
          </tr>
        </tbody>
      </table>
    </body></html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>📦 Packet Pickup</h1>
        {selectedLocation && locationCount !== null && (
          <div style={styles.countBadge}>
            {LOCATION_LABELS[selectedLocation]}: <strong>{locationCount}</strong> pickups
            {stats && <span style={styles.countTotal}> / {stats.totalExpected} expected</span>}
          </div>
        )}
      </div>

      {/* Location Selector */}
      <div style={styles.locationSection}>
        <label style={styles.locationLabel}>Pickup Location:</label>
        <div style={styles.pillContainer}>
          {PICKUP_LOCATIONS.map((loc) => (
            <button
              key={loc}
              type="button"
              style={selectedLocation === loc ? styles.pillActive : styles.pill}
              onClick={() => handleLocationChange(loc)}
              aria-pressed={selectedLocation === loc}
            >
              {LOCATION_LABELS[loc]}
            </button>
          ))}
        </div>
        {!selectedLocation && (
          <p style={styles.locationHint}>⚠️ Please select a location before processing pickups</p>
        )}
      </div>

      {/* Search Mode Toggle */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <button
          style={searchMode === 'guest' ? styles.pillActive : styles.pill}
          onClick={() => { setSearchMode('guest'); setResults([]); setOrderLabel(null); setSearchQuery(''); }}
        >
          🔍 Name / Ticket #
        </button>
        <button
          style={searchMode === 'order' ? styles.pillActive : styles.pill}
          onClick={() => { setSearchMode('order'); setResults([]); setOrderLabel(null); setSearchQuery(''); }}
        >
          📋 Order #
        </button>
      </div>

      {/* Search Input */}
      <div style={styles.searchSection}>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={searchMode === 'order' ? "Enter order reference number..." : "Enter ticket number, guest name, or scan QR code..."}
          style={styles.searchInput}
          aria-label={searchMode === 'order' ? "Search by order number" : "Search guest by ticket number, name, or QR code"}
        />
        <button
          onClick={() => handleSearch(searchQuery)}
          style={styles.searchButton}
          disabled={!searchQuery.trim() || loading}
        >
          {loading ? '...' : '🔍 Search'}
        </button>
      </div>

      {/* Success Toast */}
      {successMessage && (
        <div style={styles.successBanner} role="status">
          {successMessage}
        </div>
      )}

      {/* Error Toast */}
      {errorMessage && (
        <div style={styles.errorBanner} role="alert">
          {errorMessage}
        </div>
      )}

      {/* Search Results */}
      {results.length > 0 && (
        <div style={styles.resultsList}>
          {/* Results header with count and close button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: TEXT_MUTED }}>
              {orderLabel ? `Order: ${orderLabel} — ` : ''}{results.length} guest{results.length !== 1 ? 's' : ''}
              {results.filter(g => g.isCheckedIn).length > 0 && (
                <span style={{ color: '#16a34a', marginLeft: '0.5rem' }}>
                  ({results.filter(g => g.isCheckedIn).length} checked in)
                </span>
              )}
            </span>
            <button
              onClick={() => { setResults([]); setOrderLabel(null); setSearchQuery(''); inputRef.current?.focus(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.85rem', color: PRIMARY, fontWeight: 600, padding: '4px 8px' }}
              aria-label="Close results"
            >
              ✕ Close
            </button>
          </div>
          {results.map((guest) => (
            <div key={guest.guestRecordId} style={styles.guestCard}>
              <div style={styles.guestInfo}>
                <div style={styles.guestName}>{guest.fullName}</div>
                <div style={styles.guestMeta}>
                  <span style={styles.ticketBadge}>{guest.ticketNumber}</span>
                  {guest.chapterAffiliation && (
                    <span style={styles.metaItem}>📍 {guest.chapterAffiliation}</span>
                  )}
                  {guest.dietaryPreference && (
                    <span style={styles.metaItem}>🍽️ {guest.dietaryPreference}</span>
                  )}
                </div>
                {guest.isCheckedIn && (
                  <div style={styles.checkedInBadge}>✅ Already Checked In</div>
                )}
              </div>
              <div style={styles.guestActions}>
                <button
                  onClick={() => handleConfirmPickup(guest)}
                  disabled={!selectedLocation || guest.isCheckedIn || confirmingId === guest.guestRecordId}
                  style={
                    !selectedLocation || guest.isCheckedIn
                      ? styles.confirmButtonDisabled
                      : styles.confirmButton
                  }
                  aria-label={`Confirm pickup for ${guest.fullName}`}
                >
                  {confirmingId === guest.guestRecordId ? 'Processing...' : 'Confirm Pickup'}
                </button>
                {guest.isCheckedIn && (
                  <button
                    onClick={async () => {
                      try {
                        await apiClient('/packet-pickup/reverse', { method: 'POST', body: { guestRecordId: guest.guestRecordId } });
                        setResults((prev) => prev.map((g) => g.guestRecordId === guest.guestRecordId ? { ...g, isCheckedIn: false } : g));
                        setSuccessMessage(`↩️ ${guest.fullName} — Check-in reversed`);
                      } catch (err: any) {
                        setErrorMessage(err?.message || 'Reverse failed');
                      }
                    }}
                    style={{ marginTop: '6px', padding: '6px 14px', borderRadius: '6px', border: '1px solid #f87171', background: 'transparent', color: '#f87171', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' }}
                    aria-label={`Reverse check-in for ${guest.fullName}`}
                  >
                    ↩️ Undo Check-In
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats Summary Section */}
      {stats && (
        <div style={styles.statsSection}>
          <div style={styles.statsSectionHeader}>
            <h2 style={styles.statsTitle}>📊 Pickup Stats</h2>
            {isOrganizerPlus && (
              <button
                onClick={handlePrintReport}
                style={styles.printButton}
                aria-label="Print packet pickup report"
              >
                🖨️ Print Report
              </button>
            )}
          </div>

          {/* Progress bar */}
          <div style={styles.progressSection}>
            <div style={styles.progressLabel}>
              <span><strong>{stats.totalPickups}</strong> of <strong>{stats.totalExpected}</strong> packets picked up</span>
              <span style={styles.progressPercent}>{percentProcessed}%</span>
            </div>
            <div style={styles.progressBarBg}>
              <div
                style={{ ...styles.progressBarFill, width: `${Math.min(percentProcessed, 100)}%` }}
                role="progressbar"
                aria-valuenow={percentProcessed}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${percentProcessed}% of packets picked up`}
              />
            </div>
          </div>

          {/* Per-location breakdown */}
          <div style={styles.locationGrid}>
            {PICKUP_LOCATIONS.map((loc) => {
              const count = stats.byLocation[loc] || 0;
              return (
                <div key={loc} style={styles.locationStatCard}>
                  <div style={styles.locationStatCount}>{count}</div>
                  <div style={styles.locationStatLabel}>{LOCATION_LABELS[loc]}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '1.5rem',
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '0.5rem',
    marginBottom: '1.5rem',
  },
  title: {
    fontSize: '1.75rem',
    color: TEXT_DARK,
    margin: 0,
  },
  countBadge: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '0.5rem 1rem',
    fontSize: '0.9rem',
    color: TEXT_DARK,
  },
  countTotal: {
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
  locationSection: {
    marginBottom: '1.5rem',
  },
  locationLabel: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    marginBottom: '0.5rem',
  },
  pillContainer: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
  },
  pill: {
    padding: '0.6rem 1.2rem',
    borderRadius: '20px',
    border: `1.5px solid ${BORDER_LIGHT}`,
    backgroundColor: BG_CARD,
    color: TEXT_DARK,
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  pillActive: {
    padding: '0.6rem 1.2rem',
    borderRadius: '20px',
    border: `1.5px solid ${PRIMARY}`,
    backgroundColor: PRIMARY,
    color: '#ffffff',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  locationHint: {
    color: '#b45309',
    fontSize: '0.8rem',
    marginTop: '0.5rem',
    margin: '0.5rem 0 0 0',
  },
  searchSection: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  searchInput: {
    flex: 1,
    padding: '1rem 1.2rem',
    fontSize: '1.1rem',
    border: `1.5px solid ${BORDER_LIGHT}`,
    borderRadius: '10px',
    outline: 'none',
    backgroundColor: BG_CARD,
    color: TEXT_DARK,
  },
  searchButton: {
    padding: '1rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  successBanner: {
    backgroundColor: '#dcfce7',
    border: '1px solid #86efac',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.95rem',
    color: '#166534',
    marginBottom: '1rem',
    fontWeight: 500,
  },
  errorBanner: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.95rem',
    color: '#991b1b',
    marginBottom: '1rem',
    fontWeight: 500,
  },
  resultsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  guestCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '10px',
    padding: '1rem 1.25rem',
    flexWrap: 'wrap',
  },
  guestInfo: {
    flex: 1,
    minWidth: '200px',
  },
  guestName: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.35rem',
  },
  guestMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap',
    fontSize: '0.85rem',
    color: TEXT_MUTED,
  },
  ticketBadge: {
    backgroundColor: '#f0fdf4',
    border: '1px solid #bbf7d0',
    borderRadius: '4px',
    padding: '2px 8px',
    fontWeight: 600,
    fontSize: '0.8rem',
    color: PRIMARY,
  },
  metaItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  checkedInBadge: {
    marginTop: '0.4rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#16a34a',
  },
  guestActions: {
    flexShrink: 0,
  },
  confirmButton: {
    padding: '0.7rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 700,
    backgroundColor: '#16a34a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  confirmButtonDisabled: {
    padding: '0.7rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 700,
    backgroundColor: '#d1d5db',
    color: '#9ca3af',
    border: 'none',
    borderRadius: '8px',
    cursor: 'not-allowed',
  },
  statsSection: {
    marginTop: '2rem',
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '10px',
    padding: '1.25rem',
  },
  statsSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '1rem',
  },
  statsTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  printButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    backgroundColor: BG_CARD,
    color: TEXT_DARK,
    border: `1.5px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease',
  },
  progressSection: {
    marginBottom: '1rem',
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '0.9rem',
    color: TEXT_DARK,
    marginBottom: '0.4rem',
  },
  progressPercent: {
    fontWeight: 700,
    color: PRIMARY,
    fontSize: '0.95rem',
  },
  progressBarBg: {
    width: '100%',
    height: '10px',
    backgroundColor: '#e5e7eb',
    borderRadius: '5px',
    overflow: 'hidden',
  },
  progressBarFill: {
    height: '100%',
    backgroundColor: PRIMARY,
    borderRadius: '5px',
    transition: 'width 0.3s ease',
  },
  locationGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '0.75rem',
  },
  locationStatCard: {
    backgroundColor: BG_PAGE,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '0.75rem',
    textAlign: 'center' as const,
  },
  locationStatCount: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: PRIMARY,
  },
  locationStatLabel: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    marginTop: '0.2rem',
  },
};
